import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import solIcon from '../assets/images/sol-icon.png';
import SunWatermark from '../components/SunWatermark';
import { categories, collectionItems, processSteps, stats, woodTypes, faqs } from '../data/mockData';

gsap.registerPlugin(ScrollTrigger);

/* ─── Image base URL ─── */
const IMG = (id, suffix = 'public') =>
  `https://imagedelivery.net/xaKlCos5cTg_1RWzIu_h-A/${id}/${suffix}`;

const IMAGES = {
  hero:      IMG('a92d119e-5aa5-47ed-7bc1-18dba743f600', 'publicContain'),
  workshop:  IMG('fb4fd00d-9b0b-43f3-46b9-256a815c9b00', 'publicContain'),
  comedor:   IMG('c2b3bf20-961f-48d8-e2e4-dd1dd2d0ec00', 'public'),
  liveEdge:  IMG('31da9c3d-34b7-4ec6-d370-71125f17e300', 'publicContain'),
  recamara:  IMG('7875e675-e29f-47fe-69f2-cf263bf69c00', 'public'),
  exterior:  IMG('f5741ec8-762a-4cb7-7939-61490a958300', 'public'),
  redonda:   IMG('57a3d564-5eae-4d46-bbdf-ac701763f900', 'public'),
  sillas:    IMG('08bbce40-0101-4a0b-567b-39a2073b8b00', 'public'),
  river:     IMG('70d85220-10f5-42c0-bf9d-15199be56700', 'publicContain'),
};

const WA = 'https://wa.me/523338283729';
const PHONE = '+52 33 3828 3729';

/* ─── Custom Hooks ─── */
function useReveal(ref, options = {}) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const anim = gsap.fromTo(
      el,
      { opacity: 0, y: options.y !== undefined ? options.y : 24 },
      {
        opacity: 1,
        y: 0,
        duration: options.duration !== undefined ? options.duration : 0.65,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          toggleActions: 'play none none none',
        },
        delay: options.delay !== undefined ? options.delay : 0,
      }
    );
    return () => {
      if (anim.scrollTrigger) anim.scrollTrigger.kill();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/* ─── WhatsApp SVG Icon ─── */
function WhatsAppIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════
   1. HERO SECTION
═══════════════════════════════════════════════════════ */
function HeroSection() {
  const heroRef = useRef(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const tl = gsap.timeline({ delay: 0.2 });
    tl.from('.hero-title span', {
      opacity: 0,
      y: 50,
      stagger: 0.18,
      duration: 0.9,
      ease: 'power3.out',
    })
      .from('.hero-sub', { opacity: 0, y: 24, duration: 0.7, ease: 'power3.out' }, '-=0.4')
      .from('.hero-ctas', { opacity: 0, y: 20, duration: 0.7, ease: 'power3.out' }, '-=0.4')
      .from('.hero-scroll', { opacity: 0, duration: 0.5, ease: 'power2.out' }, '-=0.2');

    // Magnetic effect for CTA buttons
    const btns = document.querySelectorAll('[data-magnetic]');
    const handlers = [];
    btns.forEach((btn) => {
      const onMove = (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        gsap.to(btn, { x: x * 0.15, y: y * 0.15, duration: 0.3, ease: 'power2.out' });
      };
      const onLeave = () => {
        gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1,0.5)' });
      };
      btn.addEventListener('mousemove', onMove);
      btn.addEventListener('mouseleave', onLeave);
      handlers.push({ btn, onMove, onLeave });
    });

    return () => {
      tl.kill();
      handlers.forEach(({ btn, onMove, onLeave }) => {
        btn.removeEventListener('mousemove', onMove);
        btn.removeEventListener('mouseleave', onLeave);
      });
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-screen min-h-[700px] flex items-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Comedor artesanal Del Sol Muebles"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient: darker on left for text contrast, fades to transparent right */}
        <div className="absolute inset-0 bg-gradient-to-r from-wood/90 via-wood/60 to-wood/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-wood/50 via-transparent to-transparent" />
      </div>

      {/* Sun watermark — decorative top-right */}
      <div className="absolute top-[-80px] right-[-80px] text-gold pointer-events-none">
        <SunWatermark size={520} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
        <div className="max-w-3xl">
          {/* Sol icon */}
          <img
            src={solIcon}
            alt=""
            className="h-14 md:h-16 w-auto mb-10 opacity-90"
          />

          {/* Main headline */}
          <h1 className="hero-title font-serif leading-none tracking-widest mb-6">
            <span className="block text-5xl md:text-7xl lg:text-[5.5rem] text-white font-light">
              MUEBLES
            </span>
            <span className="block text-5xl md:text-7xl lg:text-[5.5rem] text-white font-light mt-1">
              ARTESANALES
            </span>
            <span className="block text-4xl md:text-5xl lg:text-6xl text-gold font-light mt-3 tracking-[0.12em]">
              EN MADERA SÓLIDA
            </span>
          </h1>

          {/* Subline */}
          <p className="hero-sub font-sans text-sm md:text-base text-cream/70 tracking-[0.22em] uppercase font-light mb-10">
            Tonalá, Jalisco&nbsp;&nbsp;·&nbsp;&nbsp;Desde el taller a tu hogar&nbsp;&nbsp;·&nbsp;&nbsp;Entrega en 2 semanas
          </p>

          {/* CTA buttons */}
          <div className="hero-ctas flex flex-wrap gap-4">
            <Link
              to="/coleccion"
              data-magnetic
              className="inline-flex items-center gap-2 font-sans text-sm tracking-[0.15em] uppercase font-medium px-8 py-4 bg-terracotta text-cream transition-all duration-300 hover:bg-terracotta-dark hover:scale-105"
            >
              Ver Catálogo
            </Link>
            <a
              href={WA}
              target="_blank"
              rel="noopener noreferrer"
              data-magnetic
              className="inline-flex items-center gap-3 font-sans text-sm tracking-[0.15em] uppercase font-medium px-8 py-4 border border-gold text-gold transition-all duration-300 hover:bg-gold hover:text-wood hover:scale-105"
            >
              <WhatsAppIcon size={18} />
              Cotizar por WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="font-sans text-[10px] text-cream/40 tracking-[0.3em] uppercase">
          Scroll
        </span>
        <div className="w-px h-12 bg-gradient-to-b from-cream/40 to-transparent" />
        <div className="animate-bounce text-cream/40">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 10.94L2.53 5.47l1.06-1.06L8 8.82l4.41-4.41 1.06 1.06L8 10.94z" />
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   2. STATS SECTION
═══════════════════════════════════════════════════════ */
function StatsSection() {
  const ref = useRef(null);
  useReveal(ref, { y: 16, duration: 0.6 });

  return (
    <section className="bg-wood py-16 md:py-20 relative overflow-hidden">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSJub25lIi8+PGxpbmUgeDE9IjAiIHkxPSI0MCIgeDI9IjQwIiB5Mj0iMCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjAuNSIvPjwvc3ZnPg==')]" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {stats.map((stat, i) => (
            <div
              key={i}
              className={`text-center px-8 py-8 ${i < stats.length - 1 ? 'border-r border-cream/10' : ''}`}
            >
              <div className="font-serif text-4xl md:text-5xl lg:text-6xl text-gold font-light mb-3 tracking-tight">
                {stat.number}
              </div>
              <div className="font-sans text-xs text-cream/50 tracking-[0.2em] uppercase font-light">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   3. ABOUT SECTION — Nuestra Fábrica
═══════════════════════════════════════════════════════ */
function AboutSection() {
  const textRef = useRef(null);
  const gridRef = useRef(null);
  useReveal(textRef, { y: 28, duration: 0.7 });
  useReveal(gridRef, { y: 28, duration: 0.7, delay: 0.15 });

  return (
    <section className="bg-cream-light py-24 md:py-32 relative overflow-hidden">
      <SunWatermark
        size={600}
        className="absolute text-terracotta -right-40 top-1/2 -translate-y-1/2"
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* Text column */}
          <div ref={textRef} className="order-2 lg:order-1">
            <span className="font-sans text-xs tracking-[0.28em] uppercase text-terracotta/80 mb-5 block font-medium">
              Artesanía &amp; Tradición
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] text-wood leading-[1.08] mb-8">
              20 Años Fabricando
              <br />
              <span className="text-terracotta">Muebles Que Duran</span>
              <br />
              Generaciones
            </h2>
            <div className="w-14 h-px bg-gold mb-8" />
            <p className="font-sans text-base text-taupe leading-relaxed mb-5">
              Nuestro taller en Tonalá, Jalisco — la capital mundial de la artesanía mexicana — es
              donde maestros carpinteros con décadas de oficio convierten maderas nobles en piezas
              únicas. Cada veta, nudo y curva natural se respeta y se celebra.
            </p>
            <p className="font-sans text-base text-taupe leading-relaxed mb-5">
              Trabajamos con parota, rosa morada, tzalam, mezquite y teca — seleccionadas por su
              belleza, durabilidad y carácter. Ninguna pieza es idéntica a otra: eso es exactamente
              lo que buscamos.
            </p>
            <p className="font-sans text-base text-taupe leading-relaxed mb-10">
              Del diseño a la entrega, somos nosotros: sin intermediarios, sin subcontratistas.
              Eso nos permite garantizar calidad en cada paso del proceso.
            </p>
            <Link
              to="/proyectos"
              className="inline-flex items-center gap-3 font-sans text-sm tracking-[0.18em] uppercase font-medium text-wood border-b border-wood/40 pb-1 hover:text-terracotta hover:border-terracotta transition-all duration-300"
            >
              Conocer Nuestra Historia
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8.22 2.97l4.78 4.78H1v1.5h12l-4.78 4.78 1.06 1.06 6.53-6.53-6.53-6.53-1.06 1.06z" />
              </svg>
            </Link>
          </div>

          {/* Image grid 2×2 */}
          <div ref={gridRef} className="order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="overflow-hidden rounded-sm shadow-xl col-span-2 h-56 md:h-72">
              <img
                src={IMAGES.workshop}
                alt="Taller Del Sol Muebles Tonalá"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="overflow-hidden rounded-sm shadow-lg h-48 md:h-56">
              <img
                src={IMAGES.liveEdge}
                alt="Mesa live edge de parota"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="overflow-hidden rounded-sm shadow-lg h-48 md:h-56">
              <img
                src={IMAGES.recamara}
                alt="Recámara en madera de parota"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   4. CATEGORIES SECTION
═══════════════════════════════════════════════════════ */

function CategoriesSection() {
  const headRef = useRef(null);
  useReveal(headRef, { y: 20, duration: 0.6 });

  return (
    <section className="bg-cream py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div ref={headRef} className="text-center mb-16 md:mb-20">
          <span className="font-sans text-xs tracking-[0.28em] uppercase text-terracotta/80 mb-5 block font-medium">
            Explora
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-wood font-light">
            Nuestra Colección
          </h2>
          <div className="w-14 h-px bg-gold mx-auto mt-7" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {categories.map((cat, i) => (
            <CategoryCard key={cat.id} cat={cat} index={i} />
          ))}
        </div>

        <div className="text-center mt-14">
          <Link
            to="/coleccion"
            className="inline-flex items-center gap-3 font-sans text-sm tracking-[0.18em] uppercase font-medium px-10 py-4 border border-wood text-wood hover:bg-wood hover:text-cream transition-all duration-300"
          >
            Ver Todo el Catálogo
          </Link>
        </div>

      </div>
    </section>
  );
}

function CategoryCard({ cat, index }) {
  const cardRef = useRef(null);
  useReveal(cardRef, { y: 30, duration: 0.65, delay: index * 0.08 });

  return (
    <Link ref={cardRef} to="/coleccion" className="group block relative overflow-hidden rounded-sm shadow-lg">
      {/* Image */}
      <div className="aspect-[4/5] overflow-hidden">
        <img
          src={cat.image}
          alt={cat.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      </div>

      {/* Persistent bottom overlay */}
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-wood/90 to-transparent" />

      {/* Text — always visible at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <h3 className="font-serif text-2xl text-white font-light mb-1">{cat.name}</h3>
        <p className="font-sans text-xs text-cream/60 tracking-wide leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300 mb-3">
          {cat.description}
        </p>
        <div className="inline-block font-sans text-[10px] tracking-[0.2em] uppercase text-gold border-b border-gold/60 pb-px">
          Explorar →
        </div>
      </div>

      {/* Gold border on hover */}
      <div className="absolute inset-0 border-2 border-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-sm" />
    </Link>
  );
}

/* ═══════════════════════════════════════════════════════
   5. WOOD TYPES SECTION — AEO / GEO
═══════════════════════════════════════════════════════ */
function WoodTypesSection() {
  const headRef = useRef(null);
  const rowRef = useRef(null);
  useReveal(headRef, { y: 20, duration: 0.6 });
  useReveal(rowRef, { y: 30, duration: 0.7, delay: 0.1 });

  return (
    <section className="bg-wood py-24 md:py-32 relative overflow-hidden">
      {/* Decorative watermark */}
      <div className="absolute bottom-[-120px] left-[-120px] text-cream opacity-[0.03] pointer-events-none">
        <SunWatermark size={640} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={headRef} className="mb-14">
          <span className="font-sans text-xs tracking-[0.28em] uppercase text-gold/70 mb-5 block font-medium">
            Materiales Premium
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-cream font-light">
            Las Maderas Que Trabajamos
          </h2>
          <div className="w-14 h-px bg-gold mt-7" />
        </div>

        {/* Horizontal scroll on mobile, flex on desktop */}
        <div
          ref={rowRef}
          className="flex gap-6 overflow-x-auto pb-4 -mx-6 px-6 lg:mx-0 lg:px-0 lg:overflow-visible lg:grid lg:grid-cols-5 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none' }}
        >
          {woodTypes.map((wood, i) => (
            <WoodCard key={wood.name} wood={wood} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WoodCard({ wood, index }) {
  return (
    <div
      className="flex-none w-64 lg:w-auto snap-start border border-cream/10 p-7 hover:border-gold/40 transition-all duration-500 group cursor-default"
      style={{ background: 'rgba(255,255,255,0.03)' }}
    >
      {/* Color swatch */}
      <div
        className="w-10 h-10 rounded-full mb-6 ring-2 ring-cream/10 group-hover:ring-gold/40 transition-all duration-500"
        style={{ background: wood.swatch }}
      />
      {/* Name */}
      <h3 className="font-serif text-2xl md:text-3xl text-gold font-light mb-1">
        {wood.name}
      </h3>
      {/* Origin */}
      <p className="font-sans text-[10px] text-cream/40 tracking-[0.2em] uppercase mb-4">
        {wood.origin}
      </p>
      {/* Divider */}
      <div className="w-8 h-px bg-gold/30 mb-5 group-hover:w-full group-hover:bg-gold/50 transition-all duration-500" />
      {/* Description */}
      <p className="font-sans text-sm text-cream/60 leading-relaxed">
        {wood.description}
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   6. FEATURED PRODUCTS — Proyectos Destacados
═══════════════════════════════════════════════════════ */

/* Override first 4 items with real images for hero display */
const featuredOverrides = [
  { ...collectionItems[0], image: IMAGES.comedor },
  { ...collectionItems[1], image: IMAGES.sillas },
  { ...collectionItems[2], image: IMAGES.exterior },
  { ...collectionItems[3], image: IMAGES.river },
];

function FeaturedProducts() {
  const headRef = useRef(null);
  useReveal(headRef, { y: 20, duration: 0.6 });

  return (
    <section className="bg-cream-light py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div ref={headRef} className="text-center mb-16 md:mb-20">
          <span className="font-sans text-xs tracking-[0.28em] uppercase text-terracotta/80 mb-5 block font-medium">
            Galería
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-wood font-light">
            Proyectos Destacados
          </h2>
          <div className="w-14 h-px bg-gold mx-auto mt-7" />
        </div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 lg:gap-8">
          {featuredOverrides.map((item, i) => (
            <FeaturedCard key={item.id} item={item} index={i} tall={i === 0 || i === 3} />
          ))}
        </div>

        <div className="text-center mt-14">
          <Link
            to="/coleccion"
            className="inline-flex items-center gap-3 font-sans text-sm tracking-[0.18em] uppercase font-medium px-10 py-4 bg-wood text-cream hover:bg-wood-light transition-all duration-300"
          >
            Ver Toda la Colección
          </Link>
        </div>

      </div>
    </section>
  );
}

function FeaturedCard({ item, index, tall }) {
  const ref = useRef(null);
  useReveal(ref, { y: 32, duration: 0.7, delay: index * 0.1 });

  const waText = `Hola, vi el ${item.name} y me gustaría cotizarlo`;
  const waLink = `${WA}?text=${encodeURIComponent(waText)}`;

  return (
    <div
      ref={ref}
      className={`group relative overflow-hidden rounded-sm shadow-xl ${tall ? 'sm:h-[480px]' : 'sm:h-[380px]'} h-[340px]`}
    >
      <img
        src={item.image}
        alt={item.name}
        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-wood/85 via-wood/30 to-transparent" />

      {/* Material tag */}
      <div className="absolute top-5 left-5">
        <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-gold border border-gold/50 px-3 py-1 backdrop-blur-sm bg-wood/30">
          {item.material}
        </span>
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-400">
        <h3 className="font-serif text-2xl md:text-3xl text-white font-light mb-2">{item.name}</h3>
        <p className="font-sans text-sm text-cream/60 leading-relaxed mb-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          {item.description}
        </p>
        <a
          href={waLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-sans text-xs tracking-[0.18em] uppercase text-white border-b border-white/40 pb-px hover:text-gold hover:border-gold transition-colors duration-300"
        >
          <WhatsAppIcon size={14} />
          Quiero algo similar
        </a>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   7. PROCESS SECTION
═══════════════════════════════════════════════════════ */
const processIcons = ['✏️', '🪚', '✨', '🚚'];

function ProcessSection() {
  const ref = useRef(null);
  useReveal(ref, { y: 24, duration: 0.7 });

  return (
    <section className="bg-cream py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <span className="font-sans text-xs tracking-[0.28em] uppercase text-terracotta/80 mb-5 block font-medium">
            Nuestro Proceso
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-wood font-light">
            Del Concepto a Tu Hogar
          </h2>
          <div className="w-14 h-px bg-gold mx-auto mt-7" />
        </div>

        {/* Steps */}
        <div ref={ref} className="relative">
          {/* Connecting dotted line — desktop only */}
          <div className="hidden lg:block absolute top-12 left-[calc(12.5%+1rem)] right-[calc(12.5%+1rem)] h-px border-t border-dashed border-gold/30" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {processSteps.map((step, i) => (
              <div key={step.step} className="relative flex flex-col items-center text-center">
                {/* Step circle */}
                <div className="relative z-10 w-24 h-24 rounded-full border-2 border-gold/40 bg-cream flex flex-col items-center justify-center mb-6 shadow-sm group-hover:border-gold transition-colors duration-300">
                  <span className="font-sans text-xs text-gold tracking-[0.15em] uppercase font-medium">
                    {String(step.step).padStart(2, '0')}
                  </span>
                  <span className="text-2xl mt-1">{processIcons[i]}</span>
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-wood mb-3 font-light">{step.title}</h3>
                <p className="font-sans text-sm text-taupe leading-relaxed max-w-[180px] mx-auto">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   8. WHATSAPP CTA BANNER
═══════════════════════════════════════════════════════ */
function WhatsAppCTASection() {
  const ref = useRef(null);
  useReveal(ref, { y: 20, duration: 0.65 });

  return (
    <section className="bg-terracotta py-20 md:py-24 relative overflow-hidden">
      {/* Decorative sun */}
      <div className="absolute right-[-100px] top-1/2 -translate-y-1/2 text-cream opacity-[0.06] pointer-events-none">
        <SunWatermark size={500} />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
        <span className="font-sans text-xs tracking-[0.28em] uppercase text-cream/60 mb-6 block font-medium">
          Trabajemos Juntos
        </span>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mb-5 max-w-3xl mx-auto leading-tight">
          ¿Tienes un proyecto en mente?
        </h2>
        <p className="font-sans text-base text-cream/75 tracking-wide mb-10 max-w-xl mx-auto">
          Cuéntanos y cotizamos gratis. Respuesta en menos de 24 horas.
          Sin compromisos, sin letra chica.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {/* WhatsApp primary CTA */}
          <a
            href={WA}
            target="_blank"
            rel="noopener noreferrer"
            data-magnetic
            className="inline-flex items-center gap-3 font-sans text-sm tracking-[0.15em] uppercase font-semibold px-10 py-4 transition-all duration-300 hover:scale-105"
            style={{ background: '#25D366', color: '#fff' }}
          >
            <WhatsAppIcon size={20} />
            Cotizar por WhatsApp
          </a>

          {/* Phone */}
          <a
            href={`tel:${PHONE.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-3 font-sans text-sm tracking-[0.12em] text-cream/80 hover:text-white transition-colors duration-300"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
            {PHONE}
          </a>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   9. FAQ SECTION — AEO Critical
═══════════════════════════════════════════════════════ */
function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const headRef = useRef(null);
  useReveal(headRef, { y: 20, duration: 0.6 });

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section className="bg-cream-light py-24 md:py-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div ref={headRef} className="text-center mb-14 md:mb-16">
          <span className="font-sans text-xs tracking-[0.28em] uppercase text-terracotta/80 mb-5 block font-medium">
            Todo Lo Que Necesitas Saber
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-wood font-light">
            Preguntas Frecuentes
          </h2>
          <div className="w-14 h-px bg-gold mx-auto mt-7" />
        </div>

        {/* Accordion */}
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <FAQItem
              key={i}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
              index={i}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14">
          <p className="font-sans text-sm text-taupe mb-5">
            ¿Tienes otra pregunta? Escríbenos directamente.
          </p>
          <a
            href={`${WA}?text=${encodeURIComponent('Hola, tengo una pregunta sobre sus muebles')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-sans text-sm tracking-[0.15em] uppercase font-medium text-terracotta border-b border-terracotta/40 pb-px hover:border-terracotta transition-colors duration-300"
          >
            <WhatsAppIcon size={15} />
            Preguntar por WhatsApp
          </a>
        </div>

      </div>
    </section>
  );
}

function FAQItem({ question, answer, isOpen, onToggle, index }) {
  const bodyRef = useRef(null);

  return (
    <div className={`border border-wood/10 transition-all duration-300 ${isOpen ? 'bg-white shadow-md' : 'bg-white/50 hover:bg-white/80'}`}>
      <button
        onClick={onToggle}
        className="w-full flex justify-between items-start gap-6 px-7 py-6 text-left"
        aria-expanded={isOpen}
      >
        <span className="font-serif text-lg md:text-xl text-wood font-light leading-snug">
          {question}
        </span>
        <span
          className="flex-none mt-1 w-7 h-7 rounded-full border border-wood/20 flex items-center justify-center transition-all duration-300"
          style={{
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            background: isOpen ? '#B4442A' : 'transparent',
            borderColor: isOpen ? '#B4442A' : undefined,
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill={isOpen ? '#fff' : '#3E2A1F'}
          >
            <line x1="6" y1="0" x2="6" y2="12" stroke="currentColor" strokeWidth="1.5" />
            <line x1="0" y1="6" x2="12" y2="6" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </span>
      </button>

      <div
        ref={bodyRef}
        style={{
          maxHeight: isOpen ? '300px' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.4s cubic-bezier(0.23,1,0.32,1)',
        }}
      >
        <p className="font-sans text-sm text-taupe leading-relaxed px-7 pb-6">
          {answer}
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   HOME PAGE — Assembly
═══════════════════════════════════════════════════════ */
export default function Home() {
  return (
    <>
      <HeroSection />
      <StatsSection />
      <AboutSection />
      <CategoriesSection />
      <WoodTypesSection />
      <FeaturedProducts />
      <ProcessSection />
      <WhatsAppCTASection />
      <FAQSection />
    </>
  );
}
